export interface DeviceInfo {
  browser: string
  browserVersion: string
  os: string
  osVersion: string
  deviceType: 'desktop' | 'mobile' | 'tablet'
  isSynced: boolean
  syncProvider?: string
  platform: string
  userAgent: string
  screenResolution: string
  timezone: string
  language: string
  authenticatorAttachment?: 'platform' | 'cross-platform'
}

const detectBrowser = (ua: string): { name: string; version: string } => {
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    const match = ua.match(/Chrome\/([\d.]+)/)
    return { name: 'Chrome', version: match?.[1]?.split('.')[0] ?? '' }
  }

  if (ua.includes('Edg')) {
    const match = ua.match(/Edg\/([\d.]+)/)
    return { name: 'Edge', version: match?.[1]?.split('.')[0] ?? '' }
  }

  if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/([\d.]+)/)
    return { name: 'Firefox', version: match?.[1]?.split('.')[0] ?? '' }
  }

  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/([\d.]+)/)
    return { name: 'Safari', version: match?.[1]?.split('.')[0] ?? '' }
  }

  if (ua.includes('OPR') || ua.includes('Opera')) {
    const match = ua.match(/(?:OPR|Opera)\/([\d.]+)/)
    return { name: 'Opera', version: match?.[1]?.split('.')[0] ?? '' }
  }

  return { name: 'Unknown Browser', version: '' }
}

const detectOS = (ua: string, platform: string): { name: string; version: string } => {
  if (/iPad|iPhone|iPod/.test(ua)) {
    const match = ua.match(/OS ([\d_]+)/)
    const version = match?.[1]?.replace(/_/g, '.').split('.').slice(0, 2).join('.') ?? ''
    if (ua.includes('iPad')) {
      return { name: 'iPadOS', version }
    }
    return { name: 'iOS', version }
  }

  if (platform.startsWith('Mac')) {
    const match = ua.match(/Mac OS X ([\d_]+)/)
    const version = match?.[1]?.replace(/_/g, '.').split('.').slice(0, 2).join('.') ?? ''
    return { name: 'macOS', version }
  }

  if (platform.startsWith('Win')) {
    if (ua.includes('Windows NT 10.0') && ua.includes('Win64')) {
      return { name: 'Windows', version: '11' }
    }
    if (ua.includes('Windows NT 10.0')) {
      return { name: 'Windows', version: '10' }
    }
    return { name: 'Windows', version: '' }
  }

  if (ua.includes('Android')) {
    const match = ua.match(/Android ([\d.]+)/)
    const version = match?.[1]?.split('.')[0] ?? ''
    return { name: 'Android', version }
  }

  if (platform.startsWith('Linux')) {
    return { name: 'Linux', version: '' }
  }

  return { name: 'Unknown OS', version: '' }
}

const detectDeviceType = (ua: string): 'desktop' | 'mobile' | 'tablet' => {
  if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) {
    return 'tablet'
  }

  if (/iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|Opera Mini|IEMobile/.test(ua)) {
    return 'mobile'
  }

  if (typeof window !== 'undefined') {
    if (window.innerWidth <= 768 && 'ontouchstart' in window) {
      return 'mobile'
    }

    if (window.innerWidth <= 1024 && 'ontouchstart' in window) {
      return 'tablet'
    }
  }

  return 'desktop'
}

const detectSyncProvider = (
  os: string,
  browser: string,
  authenticatorAttachment?: string
): { isSynced: boolean; provider?: string } => {
  if (authenticatorAttachment === 'cross-platform') {
    return { isSynced: false, provider: 'Security Key' }
  }

  if (os.includes('iOS') || os.includes('iPadOS') || os.includes('macOS')) {
    return { isSynced: true, provider: 'iCloud Keychain' }
  }

  if (
    browser === 'Chrome' &&
    (os.includes('Android') || os.includes('Windows') || os.includes('macOS'))
  ) {
    return { isSynced: true, provider: 'Google Password Manager' }
  }

  if (os.includes('Windows') && (browser === 'Edge' || browser === 'Chrome')) {
    return { isSynced: true, provider: 'Windows Hello' }
  }

  if (os.includes('Android') && navigator.userAgent.includes('Samsung')) {
    return { isSynced: true, provider: 'Samsung Pass' }
  }

  return { isSynced: false }
}

export const getDeviceInfo = (
  authenticatorAttachment?: 'platform' | 'cross-platform'
): DeviceInfo => {
  const ua = navigator.userAgent
  const platform = navigator.platform ?? ''
  const browser = detectBrowser(ua)
  const os = detectOS(ua, platform)
  const deviceType = detectDeviceType(ua)
  const syncInfo = detectSyncProvider(os.name, browser.name, authenticatorAttachment)

  const screenResolution = `${window.screen.width}x${window.screen.height}`
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const language = navigator.language || 'en-US'

  return {
    browser: browser.name,
    browserVersion: browser.version,
    os: os.name,
    osVersion: os.version,
    deviceType,
    isSynced: syncInfo.isSynced,
    syncProvider: syncInfo.provider,
    platform,
    userAgent: ua,
    screenResolution,
    timezone,
    language,
    authenticatorAttachment
  }
}

export const suggestDeviceName = (info: DeviceInfo): string => {
  const prefix =
    info.deviceType === 'mobile' ? 'Phone' : info.deviceType === 'tablet' ? 'Tablet' : 'Computer'
  return `${prefix} - ${info.browser} on ${info.os}`
}
